import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { RicosContext } from './../../../../../ricos-context';

import { getToolbarGradientPercents } from './get-toolbar-gradient-percents';
import classes from './mobile-toolbar-overlay-scss';

type MobileToolbarOverlayProps = {
  children: React.ReactNode;
  mobileToolbar: HTMLDivElement | null;
  maxWidth: number;
};

export const MobileToolbarOverlay = ({
  children,
  mobileToolbar,
  maxWidth,
}: MobileToolbarOverlayProps) => {
  const { languageDir } = useContext(RicosContext);
  const lastCalculatedMobileToolbarWidth = useRef(0);
  const [gradientWidths, setGradientWidths] = useState(() =>
    getToolbarGradientPercents(mobileToolbar, languageDir === 'rtl')
  );

  const toolbarParams = useMemo(() => {
    if (!mobileToolbar) {
      return {
        height: 0,
        topOffset: 0,
        width: 0,
      };
    }
    return {
      height: mobileToolbar.clientHeight,
      topOffset: parseInt(getComputedStyle(mobileToolbar).getPropertyValue('border-top-width')),
      width: mobileToolbar.offsetWidth,
    };
  }, [mobileToolbar, maxWidth]);

  useEffect(() => {
    if (!mobileToolbar || !window?.ResizeObserver) {
      return;
    }
    const recalculateGradients = () => {
      lastCalculatedMobileToolbarWidth.current = mobileToolbar.clientWidth;
      setGradientWidths(getToolbarGradientPercents(mobileToolbar, languageDir === 'rtl'));
    };

    mobileToolbar.addEventListener('scroll', recalculateGradients);
    recalculateGradients();
    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (entry.contentRect.width !== lastCalculatedMobileToolbarWidth.current) {
          recalculateGradients();
        }
      });
    });

    resizeObserver.observe(mobileToolbar);

    return () => {
      mobileToolbar.removeEventListener('scroll', recalculateGradients);
      resizeObserver.disconnect();
    };
  }, [mobileToolbar, toolbarParams]);

  if (gradientWidths === undefined) {
    return <>{children}</>;
  }

  return (
    <div
      className={classes.mobileToolbarOverlay}
      style={{
        ['--mobile-toolbar-width' as string]: toolbarParams.width,
        ['--left-gradient-end' as string]: gradientWidths.leftGradientEndPercent,
        ['--right-gradient-start' as string]: gradientWidths.rightGradientStartPercent,
      }}
    >
      {children}
    </div>
  );
};
